import {
  CoreAlertModal,
  type AlertSeverity,
  type HeadingLevel,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import {
  ErrorIcon,
  WarningIcon,
  SuccessIcon,
  InfoIcon,
} from "@boi/react-icons";

import * as S from "./AlertModal.styled";
import { useMatchMedia, useOrderedButtonsProps } from "./AlertModal.utils";
import { Modal, type ModalProps } from "../Modal";
import { Typography } from "../Typography";
import { Button, type ButtonProps } from "../Button";
import { ScrollShadowContainer } from "../ScrollShadowContainer";

export type AlertModalProps = Omit<ModalProps, "onRequestClose"> & {
  heading: string;
  headingLevel?: HeadingLevel;
  severity?: AlertSeverity;
  primaryButtonsProps?: Omit<ButtonProps, "variant" | "fullWidth">[];
  secondaryButtonsProps?: Omit<ButtonProps, "variant" | "fullWidth">[];
  footerLayout?: CoreAlertModal.FooterLayout;
  hasStickyFooter?: boolean;
};

function AlertModal(props: AlertModalProps) {
  const {
    heading,
    headingLevel = CoreAlertModal.DEFAULT_HEADING_LEVEL,
    severity = CoreAlertModal.DEFAULT_SEVERITY,
    primaryButtonsProps,
    secondaryButtonsProps,
    footerLayout = CoreAlertModal.DEFAULT_FOOTER_LAYOUT,
    hasStickyFooter = false,
    children,
    testID = "alert-modal",
    ...modalProps
  } = props;

  const { isOpen } = modalProps;

  const { tokens } = useDesignTokens();
  const { alertModal } = tokens;

  const alertIcon = {
    error: <ErrorIcon />,
    warning: <WarningIcon />,
    success: <SuccessIcon />,
    info: <InfoIcon />,
  };

  const { isMobile } = useMatchMedia();

  const { orderedButtonsProps } = useOrderedButtonsProps(
    footerLayout,
    primaryButtonsProps,
    secondaryButtonsProps
  );

  const footer = (
    <div>
      <S.Footer
        $layout={isMobile ? "stacked" : footerLayout}
        $stickFooter={hasStickyFooter}
      >
        {orderedButtonsProps.map((buttonProps) => (
          <Button key={buttonProps.text} {...buttonProps} />
        ))}
      </S.Footer>
    </div>
  );

  return (
    <Modal aria-label={heading} testID={testID} {...modalProps}>
      {isOpen && (
        <S.Container>
          <S.Header $severity={severity}>
            <S.IconWrapper $severity={severity}>
              {alertIcon[severity]}
            </S.IconWrapper>

            <Typography
              component={`h${headingLevel}`}
              variant={alertModal.heading.typography.variant}
              fontFamily={alertModal.heading.typography.fontFamily}
            >
              {heading}
            </Typography>
          </S.Header>

          <ScrollShadowContainer>
            <S.Body $stickFooter={hasStickyFooter}>{children}</S.Body>
            {!hasStickyFooter ? footer : undefined}
          </ScrollShadowContainer>

          {hasStickyFooter ? footer : undefined}
        </S.Container>
      )}
    </Modal>
  );
}

export default AlertModal;
